import axios from 'axios';
import { Cookies } from 'react-cookie';
import { API_BACKEND } from './backendUrl';

/**
 * Сессия админки: access-токен (JWT, 15 дней) + refresh-токен (90 дней).
 *
 * Access лежит в куке `token` — её читают десятки мест (useCookies, сокеты).
 * Refresh — httpOnly-кука на back.kanuda.pro: JS её не видит, браузер сам отправляет
 * её на /auth/refresh и /auth/logout (запросы с withCredentials).
 *
 * Правила:
 *  - access старше суток обновляется перед запросом — так refresh-сессия продлевается
 *    при каждом визите и выкидывает на логин только после 90 дней без захода;
 *  - 401 от API → одна попытка обновить токен и повторить запрос;
 *  - на логин отправляем, только когда /auth/refresh ответил 401/403 (сессии нет).
 *    Сетевая ошибка или 5xx сессию не трогает — повторим при следующем запросе;
 *  - обновление одно на все вкладки (Web Locks), иначе две вкладки предъявили бы
 *    один refresh-токен, а повтор старого токена сервер считает кражей.
 */

/**
 * Общая банка кук приложения — её же получает CookiesProvider (src/index.js), поэтому
 * запись токена отсюда сразу видна useCookies (роутинг, useCurrentUser).
 * path '/' по умолчанию — иначе removeCookie на вложенной странице не удалял куку.
 */
export const cookieJar = new Cookies(null, { path: '/' });

const ACCESS_COOKIE = 'token';
const PROFILE_COOKIES = ['name', 'first_name', 'last_name', 'phone', 'tag', 'user_type', 'widget_id', 'avatar', 'city'];
/** Обновлять access-токен, когда ему больше суток. */
const RENEW_AFTER_SEC = 24 * 60 * 60;
/** Запас до истечения: токен, которому осталось меньше минуты, уже не отправляем. */
const EXPIRY_SKEW_SEC = 60;
/** Метка «пользователь вышел сам» — после неё не пытаемся тихо восстановить сессию. */
const SIGNED_OUT_KEY = 'kanuda_admin_signed_out';
const AUTH_PATH_RE = /^(https?:\/\/[^/]+)?\/?auth\/(login|refresh|logout|register|admin-reset)\b/;

/** /auth/refresh ответил 401/403 — сессии нет, нужен логин. */
export class SessionExpiredError extends Error {
  constructor() {
    super('Сессия истекла — войдите снова');
    this.name = 'SessionExpiredError';
  }
}

/** Разница «часы сервера − часы браузера», сек. Берём из iat свежевыданного токена. */
let clockOffsetSec = 0;
/** Refresh в этой вкладке уже ответил «сессии нет» — без токена не долбим /auth/refresh. */
let sessionDead = false;
let refreshInFlight = null;

const nowSec = () => Date.now() / 1000 + clockOffsetSec;

function decodeJwt(token) {
  try {
    const part = String(token).split('.')[1];
    const base64 = part.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base64.padEnd(Math.ceil(base64.length / 4) * 4, '=')));
  } catch {
    return null;
  }
}

function isExpired(token) {
  const exp = decodeJwt(token)?.exp;
  return !exp || exp - nowSec() < EXPIRY_SKEW_SEC;
}

function needsRenewal(token) {
  const payload = decodeJwt(token);
  if (!payload?.exp || payload.exp - nowSec() < EXPIRY_SKEW_SEC) return true;
  // Токены, выпущенные до появления refresh, обновить нечем — работают до своего срока.
  if (!payload.sid) return false;
  return !!payload.iat && nowSec() - payload.iat > RENEW_AFTER_SEC;
}

function readFlag() {
  try {
    return window.localStorage.getItem(SIGNED_OUT_KEY) === '1';
  } catch {
    return false;
  }
}

function writeFlag(on) {
  try {
    if (on) window.localStorage.setItem(SIGNED_OUT_KEY, '1');
    else window.localStorage.removeItem(SIGNED_OUT_KEY);
  } catch {
    /* приватный режим — без метки, не страшно */
  }
}

export function isAuthEndpoint(url) {
  return AUTH_PATH_RE.test(String(url || ''));
}

export function getAccessToken() {
  const token = cookieJar.get(ACCESS_COOKIE);
  return token && token !== 'null' && token !== 'undefined' ? String(token) : null;
}

function storeAccessToken(token) {
  const payload = decodeJwt(token);
  if (payload?.iat) clockOffsetSec = payload.iat - Date.now() / 1000;
  const maxAge = payload?.exp ? Math.max(Math.floor(payload.exp - nowSec()), 60) : 15 * 24 * 60 * 60;
  cookieJar.set(ACCESS_COOKIE, token, {
    path: '/',
    maxAge,
    secure: window.location.protocol === 'https:',
    sameSite: 'strict',
  });
}

/** После успешного /auth/login: refresh-куку уже поставил сервер, сохраняем access. */
export function startSession(token) {
  storeAccessToken(token);
  sessionDead = false;
  writeFlag(false);
}

/** Стереть токен и профильные куки этого браузера (серверную сессию не трогает). */
export function clearLocalSession() {
  sessionDead = true;
  writeFlag(true);
  for (const name of [ACCESS_COOKIE, ...PROFILE_COOKIES]) cookieJar.remove(name, { path: '/' });
}

/** Сессия кончилась: чистим и отправляем на логин. */
export function endSessionAndRedirect() {
  clearLocalSession();
  if (window.location.pathname !== '/login') window.location.href = '/login';
}

async function requestNewAccessToken() {
  try {
    const res = await axios.post(`${API_BACKEND}/auth/refresh`, null, { withCredentials: true, timeout: 20000 });
    const token = res.data?.token;
    if (!token) throw new Error('Сервер не вернул токен');
    startSession(token);
    return token;
  } catch (err) {
    const status = err?.response?.status;
    if (status === 401 || status === 403) {
      sessionDead = true;
      throw new SessionExpiredError();
    }
    throw err;
  }
}

/**
 * Получить новый access-токен. usedToken — токен, с которым запрос не прошёл:
 * если в куке уже другой живой токен (обновила другая вкладка), сервер не трогаем.
 */
export function refreshAccessToken(usedToken = getAccessToken()) {
  if (!refreshInFlight) {
    const run = async () => {
      const current = getAccessToken();
      if (current && current !== usedToken && !isExpired(current)) return current;
      return requestNewAccessToken();
    };
    const locked =
      typeof navigator !== 'undefined' && navigator.locks?.request
        ? navigator.locks.request('kanuda-admin-token-refresh', run)
        : run();
    refreshInFlight = locked.finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

/**
 * Токен для запроса: живой — как есть, старый или отсутствующий — сперва обновить.
 * Если обновить не вышло, отдаём что есть: сервер ответит 401, и запрос разберётся сам.
 */
export async function ensureAccessToken() {
  const token = getAccessToken();
  if (token && !needsRenewal(token)) return token;
  // Обновлять нечем: refresh уже ответил «сессии нет» или пользователь вышел сам.
  // Живой access работает до своего срока, просроченный получит 401 и уйдёт на логин.
  if (sessionDead || (!token && readFlag())) return token;
  try {
    return await refreshAccessToken(token);
  } catch {
    return getAccessToken();
  }
}

/** При старте приложения без access-куки: можно ли тихо восстановить сессию по refresh. */
export function canRestoreSession() {
  return !getAccessToken() && !sessionDead && !readFlag();
}

/** Для socket.io `auth`: вызывается на каждое (пере)подключение, токен всегда свежий. */
export function socketAuth(cb) {
  ensureAccessToken()
    .catch(() => null)
    .then(() => cb({ token: getAccessToken() || '' }));
}

/**
 * Выход. По умолчанию — только этот браузер. everywhere — все устройства пользователя
 * (ошибку пробрасываем: экран покажет, что не получилось).
 */
export async function logout({ everywhere = false } = {}) {
  const token = await ensureAccessToken();
  const headers = token ? { authorization: 'Bearer ' + token } : {};
  if (everywhere) await axios.post(`${API_BACKEND}/users/me/logout`, null, { headers, timeout: 20000 });
  try {
    await axios.post(`${API_BACKEND}/auth/logout`, null, { headers, withCredentials: true, timeout: 10000 });
  } catch {
    // Не достучались — локально выходим всё равно; метка «вышел» не даст тихо войти обратно.
  }
  clearLocalSession();
}
